import { getUserLocale } from '.';

type Currency = 'USD' | 'EUR' | 'GBP' | 'INR' | (string & {});

type RoundingMode = 'floor' | 'ceil' | 'round';

interface FormatCurrencyOptions {
  currency?: Currency; // Currency code
  decimalPlaces?: number; // Set decimal places
  compact?: boolean; // Enable compact notation (K, M, B)
  roundingMode?: RoundingMode; // Choose rounding strategy
  locale?: string; // Locale to override user locale
  showDecimalsIfPresent?: boolean; // Show decimals only if they exist (e.g., 2.5 shows as "2.5", 10 shows as "10")
  maxDecimalPlaces?: number; // When showDecimalsIfPresent is true, max fraction digits to show (default 2)
}

export const formatCurrency = (
  amount: number,
  options: FormatCurrencyOptions = {},
) => {
  const locale = options.locale || getUserLocale();
  const rawDecimalPlaces = options.decimalPlaces ?? 0;
  const rawMaxDecimalPlaces = options.maxDecimalPlaces ?? 2;
  const decimalPlaces = Math.max(0, rawDecimalPlaces);
  const maxDecimalPlaces = Math.max(0, rawMaxDecimalPlaces);
  const {
    compact = false,
    roundingMode = 'round',
    showDecimalsIfPresent = false,
  } = options;

  const getRoundingFunction = (mode: RoundingMode) => {
    switch (mode) {
      case 'floor':
        return Math.floor;
      case 'ceil':
        return Math.ceil;
      default:
        return Math.round;
    }
  };

  const roundFn = getRoundingFunction(roundingMode);

  // If showDecimalsIfPresent is true, don't round - preserve original decimals
  const roundedAmount = showDecimalsIfPresent
    ? amount
    : roundFn(amount * 10 ** decimalPlaces) / 10 ** decimalPlaces;

  // Create Intl.NumberFormat options
  const formatterOptions: Intl.NumberFormatOptions = {
    style: options.currency ? 'currency' : 'decimal',
    currency: options.currency,
    minimumFractionDigits: showDecimalsIfPresent ? 0 : decimalPlaces,
    maximumFractionDigits: showDecimalsIfPresent
      ? maxDecimalPlaces
      : decimalPlaces,
    notation: compact ? 'compact' : 'standard',
  };

  return new Intl.NumberFormat(locale, formatterOptions).format(roundedAmount);
};
